import { z } from 'zod';
/**
 * Studio de design de carte (permission `carte.design`, réservée à
 * SUPER_ADMIN/ADMIN_NATIONAL — voir `role-permissions.ts`) : gabarit visuel
 * de la carte sociale, recto et verso, édité librement (position, taille,
 * texte, police, couleur, opacité, calques, import d'image) puis publié.
 *
 * Un seul gabarit est `publie` à la fois (voir `carte-templates.routes.ts`,
 * `POST /carte-templates/:uuid/publier`) : c'est celui que
 * `CartePrintPreview` (`apps/web/src/features/cartes/CarteTab.tsx`) rend
 * réellement pour chaque carte. Le gabarit par défaut
 * (`constants/default-carte-template.ts`) reproduit à l'identique le bon à
 * tirer approuvé par la CNSS — il reste utilisé tant qu'aucun gabarit n'a
 * été publié, jamais recalculé depuis zéro.
 *
 * Dimensions physiques (85,6 × 53,98 mm, format CR-80) et cadre/équerres de
 * coin sont fixes, gérés par le renderer (`CarteTemplateRenderer.tsx`), pas
 * par ce schéma — ce sont des contraintes matérielles, pas un choix éditorial.
 */
export const CARTE_ELEMENT_FONT_FAMILIES = ['Arial', 'Georgia', 'Times New Roman', 'Courier New'];
const carteElementBaseSchema = z.object({
    id: z.string().min(1),
    x: z.number(),
    y: z.number(),
    width: z.number().positive(),
    height: z.number().positive(),
    zIndex: z.number().int(),
    opacity: z.number().min(0).max(1),
    rotationDeg: z.number().default(0),
    /**
     * `null` : toujours visible. `true`/`false` : uniquement sur la variante
     * avec puce / sans puce (bon à tirer REF-02) — remplace les conditions
     * `avecPuce &&` codées en dur dans l'ancienne version de `CarteTab.tsx`.
     */
    visibleSiAvecPuce: z.boolean().nullable().default(null),
});
const carteElementTexteSchema = carteElementBaseSchema.extend({
    type: z.literal('texte'),
    contenu: z.string().max(500),
    fontFamily: z.enum(CARTE_ELEMENT_FONT_FAMILIES),
    fontSizePx: z.number().positive(),
    fontWeight: z.enum(['normal', 'bold']),
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Couleur hexadécimale attendue (#rrggbb)'),
    align: z.enum(['left', 'center', 'right']),
    transform: z.enum(['none', 'uppercase']),
});
const carteElementImageSourceSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('builtin'), name: z.string().min(1) }),
    z.object({ kind: z.literal('upload'), assetUuid: z.string().uuid() }),
]);
const carteElementImageSchema = carteElementBaseSchema.extend({
    type: z.literal('image'),
    source: carteElementImageSourceSchema,
    objectFit: z.enum(['cover', 'contain']),
    borderRadiusMm: z.number().min(0).default(0),
});
/** Données vivantes (photo/QR) : ni contenu ni source éditables, seule la mise en page l'est. */
const carteElementQrcodeSchema = carteElementBaseSchema.extend({ type: z.literal('qrcode') });
const carteElementPhotoTravailleurSchema = carteElementBaseSchema.extend({ type: z.literal('photo_travailleur') });
export const carteElementSchema = z.discriminatedUnion('type', [
    carteElementTexteSchema,
    carteElementImageSchema,
    carteElementQrcodeSchema,
    carteElementPhotoTravailleurSchema,
]);
export const carteFaceTemplateSchema = z.object({
    elements: z.array(carteElementSchema),
});
export const carteTemplateContenuSchema = z.object({
    recto: carteFaceTemplateSchema,
    verso: carteFaceTemplateSchema,
});
export const createCarteTemplateSchema = z.object({
    nom: z.string().min(1).max(160),
    dupliquerDepuisUuid: z.string().uuid().optional(),
});
export const updateCarteTemplateDraftSchema = z.object({
    nom: z.string().min(1).max(160).optional(),
    contenu: carteTemplateContenuSchema.optional(),
});
//# sourceMappingURL=carte-templates.js.map